/**
 * Self-hosted Web Push client.
 *
 * Talks to `ownerflow.api.push` over VAPID — no Firebase, no relay server. The
 * subscription lives in the browser's push manager; the server only keeps the
 * endpoint plus its two keys so it can encrypt payloads for this device.
 */

const SUBSCRIBED_FLAG_KEY = "ownerflow_push_subscribed";
const REGISTRATION_WAIT_MS = 5000;

/** "unknown" means the service worker had not settled; do not treat it as "off". */
export type DeviceSubscriptionState = "subscribed" | "unsubscribed" | "unknown";

export type PushEnableResult = {
  permission_granted: boolean;
  subscribed: boolean;
};

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** iOS only exposes push to a PWA launched from the home screen. */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone === true;
}

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** True when the device can subscribe but the user has to install the PWA first. */
export function needsHomeScreenInstall(): boolean {
  return isIos() && !isStandaloneDisplay() && !isPushSupported();
}

function csrfHeaders(): Record<string, string> {
  const token = (window as typeof window & { csrf_token?: string }).csrf_token || "";
  return token ? { "X-Frappe-CSRF-Token": token } : {};
}

async function callApi<T>(method: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api/method/${method}`, {
    method: body === undefined ? "GET" : "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...csrfHeaders(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`${method} failed with ${response.status}`);
  }
  const json = (await response.json()) as { message: T };
  return json.message;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

type PushMessagePayload = {
  notification?: { title?: string; body?: string };
  data?: { title?: string; body?: string; url?: string; link?: string; click_action?: string };
};

class OwnerFlowPushNotification {
  private publicKey: string | null = null;
  private serverEnabled: boolean | null = null;
  private registration: ServiceWorkerRegistration | null = null;
  private onMessageHandler: ((payload: PushMessagePayload) => void) | null = null;
  private messageListenerAttached = false;

  async initialize(registration: ServiceWorkerRegistration): Promise<void> {
    this.registration = registration;
    this.attachMessageListener();
    if (this.serverEnabled === null) {
      try {
        const status = await callApi<{ enabled: boolean; public_key: string | null }>(
          "ownerflow.api.push.get_public_key",
        );
        this.serverEnabled = status.enabled;
        this.publicKey = status.public_key;
      } catch {
        this.serverEnabled = false;
      }
    }
    // Keep the sync flag honest: the browser may have dropped the subscription.
    void this.refreshSubscribedFlag();
  }

  /** Foreground notifications: the service worker relays each push to open tabs. */
  onMessage(callback: (payload: PushMessagePayload) => void): void {
    this.onMessageHandler = callback;
    this.attachMessageListener();
  }

  private attachMessageListener(): void {
    if (this.messageListenerAttached || !("serviceWorker" in navigator)) return;
    this.messageListenerAttached = true;
    navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
      const data = event.data as { type?: string; payload?: PushMessagePayload } | undefined;
      if (data?.type !== "OWNERFLOW_PUSH" || !data.payload) return;
      this.onMessageHandler?.(data.payload);
    });
  }

  isServerEnabled(): boolean {
    return this.serverEnabled === true;
  }

  /** Synchronous best-effort mirror of the real subscription, for form state. */
  isNotificationEnabled(): boolean {
    return localStorage.getItem(SUBSCRIBED_FLAG_KEY) === "1";
  }

  private async getRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.registration) return this.registration;
    if (!("serviceWorker" in navigator)) return null;

    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) {
      this.registration = existing;
      return existing;
    }

    // First load after an install: registration is still in flight. Wait for it,
    // but never indefinitely — `ready` never settles if registration failed.
    this.registration = await Promise.race([
      navigator.serviceWorker.ready.catch(() => null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), REGISTRATION_WAIT_MS)),
    ]);
    return this.registration;
  }

  /**
   * Authoritative per-device check, unlike the cached localStorage flag.
   *
   * Returns "unknown" rather than guessing when the service worker has not
   * settled yet — callers that nag the user must not do so on a false negative.
   */
  async getDeviceSubscriptionState(): Promise<DeviceSubscriptionState> {
    if (!isPushSupported()) return "unsubscribed";
    if (Notification.permission !== "granted") return "unsubscribed";

    const registration = await this.getRegistration();
    if (!registration) return "unknown";

    try {
      return (await registration.pushManager.getSubscription()) ? "subscribed" : "unsubscribed";
    } catch {
      return "unknown";
    }
  }

  private async refreshSubscribedFlag(): Promise<boolean> {
    const registration = await this.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    const active = Boolean(subscription) && Notification.permission === "granted";
    localStorage.setItem(SUBSCRIBED_FLAG_KEY, active ? "1" : "0");
    return active;
  }

  private async fetchPublicKey(): Promise<string> {
    if (this.publicKey) return this.publicKey;
    const status = await callApi<{ enabled: boolean; public_key: string | null }>(
      "ownerflow.api.push.get_public_key",
    );
    this.serverEnabled = status.enabled;
    if (!status.enabled || !status.public_key) {
      throw new Error("Push notifications are disabled on this site");
    }
    this.publicKey = status.public_key;
    return this.publicKey;
  }

  async enableNotification(): Promise<PushEnableResult> {
    if (!isPushSupported()) {
      throw new Error("Push notifications are not supported on this device");
    }

    // Must be the first await of the user-gesture chain — Safari revokes the
    // gesture once an unrelated promise resolves first.
    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();

    if (permission !== "granted") {
      localStorage.setItem(SUBSCRIBED_FLAG_KEY, "0");
      return { permission_granted: false, subscribed: false };
    }

    const registration = await this.getRegistration();
    if (!registration) {
      throw new Error("Service worker is not registered yet");
    }

    const publicKey = await this.fetchPublicKey();
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // A rotated VAPID key makes the old subscription undeliverable.
      const currentKey = subscription.options?.applicationServerKey;
      const expected = urlBase64ToUint8Array(publicKey);
      if (currentKey && !arraysEqual(new Uint8Array(currentKey), expected)) {
        await subscription.unsubscribe();
        subscription = null;
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }

    await callApi<{ success: boolean }>("ownerflow.api.push.subscribe", {
      subscription: subscription.toJSON(),
    });

    localStorage.setItem(SUBSCRIBED_FLAG_KEY, "1");
    return { permission_granted: true, subscribed: true };
  }

  async disableNotification(): Promise<void> {
    localStorage.setItem(SUBSCRIBED_FLAG_KEY, "0");
    const registration = await this.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    try {
      await subscription.unsubscribe();
    } catch {
      // The browser may already have revoked it.
    }
    try {
      await callApi("ownerflow.api.push.unsubscribe", { endpoint });
    } catch {
      // Best effort; the server prunes dead endpoints on the next send anyway.
    }
  }

  /** Re-post an existing subscription so the server keeps it tied to this user. */
  async syncExistingSubscription(): Promise<void> {
    if (!isPushSupported() || Notification.permission !== "granted") return;
    const registration = await this.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      localStorage.setItem(SUBSCRIBED_FLAG_KEY, "0");
      return;
    }
    await callApi("ownerflow.api.push.subscribe", { subscription: subscription.toJSON() });
    localStorage.setItem(SUBSCRIBED_FLAG_KEY, "1");
  }

  async sendTestNotification(): Promise<{ delivered: number; devices: number }> {
    return callApi<{ delivered: number; devices: number }>("ownerflow.api.push.send_test", {});
  }
}

function arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export const ownerFlowPushNotification = new OwnerFlowPushNotification();
